import { useState, useEffect } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Drawer, Typography } from '@material-ui/core';
import { Stack, Chip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PhoneCall, Clock, User, Heart, Headphones, FileText, Megaphone, Scroll } from "lucide-react";
import { format } from "date-fns";
import { customerApi } from '../../../api/customerApi';

const CustomerCallDetailsPanel = ({ callId, openDrawer, setOpenDrawer }) => {
    const [callData, setCallData] = useState({
        user_name : '',
        user_id : 0,
        direction : 'outbound',
        phone_number : '',
        duration : 0,
        recording_short_url : "https://apteed.syndays.fr",
        sentiment : "",
        topics : "",
        started_at : '1970-01-01 00:00:00'
    });
    const [transcription, setTranscription] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedTranscript, setExpandedTranscript] = useState(false);

    const handleClose = () => {
        setOpenDrawer(false);
      };

      // Fonction pour formater le temps en jour heure minutes secondes
      const formatTime = (seconds) => {
        const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  let result = "";
  if (days > 0) result += `${days}j `;
  if (days > 0 || hours > 0) result += `${hours}h `;
  if (days > 0 || hours > 0 || minutes > 0) result += `${minutes}m `;
  result += `${secs}s`;

  return result.trim();
      };

      
  useEffect( async () => {
    setLoading(true);
    if (callId) {
      try {
     const data = await customerApi.fetchCallDetails(callId, localStorage.getItem('accessToken'));
     if (data.length === 0) {
      throw new Error('L\'appel n\'existe pas');
    }
      switch (data.data.call.sentiment) {
        case "POSITIVE":
          data.data.call.sentiment = 'POSITIF';
          break; // Vert
        case "NEGATIVE":
          data.data.call.sentiment = 'NÉGATIF';
          break; // Rouge
        case "NEUTRAL":
          data.data.call.sentiment = 'NEUTRE';
          break; // Gris
          default:
            data.data.call.sentiment = '';
            break;
      }
      setCallData(data.data.call);
      setTranscription(data.data.transcription);
  } catch (err) {
    console.error(err);
  }
    }
    setLoading(false);
  }, [callId]);

  if (!callId) return null;

  const getChipStyle = (type) => {
    switch (type) {
      case "POSITIF":
        return { backgroundColor: "#4CAF50", color: "#fff" }; // Vert
      case "NÉGATIF":
        return { backgroundColor: "#F44336", color: "#fff" }; // Rouge
      case "NEUTRE":
        return { backgroundColor: "#9E9E9E", color: "#fff" }; // Gris
      default:
        return { backgroundColor: "#E0E0E0", color: "#000" }; // Par défaut : gris clair
    }
  };
  const chipStyle = getChipStyle(callData.sentiment);

  const TranscriptionDisplay = () => {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="space-y-4">
          {transcription.map((item, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="bg-white rounded-2xl shadow p-4 border border-gray-100 hover:shadow-md transition"
            >
              <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                <span>
                  🕒 {parseFloat(item.start_time).toFixed(2)}s –{" "}
                  {parseFloat(item.end_time).toFixed(2)}s{" "}
                </span>
                 
                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs uppercase tracking-wide">
                  {item.participant_type === 'external' ? <Chip
                  key={transcription.start_time}
                  label="Interlocuteur"
                  sx={{
                    backgroundColor: "#92b9f7", color: "#fff" ,
                    fontWeight: "bold",
                    borderRadius: "16px",
                  }}
                /> : <Chip
                key={transcription.start_time}
                label={callData.user_name}
                sx={{
                  backgroundColor: "#c7ffc7", color: "#fefefe" ,
                  fontWeight: "bold",
                  borderRadius: "16px",
                }}
              />}
                </span>
              </div>
              <p className="text-gray-800 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  
return (
  
    <Drawer anchor="right" open={openDrawer} onClose={handleClose} PaperProps={{ sx: { width: "30%" } }}>
      <div className="p-6 overflow-auto h-full">
        <IconButton onClick={handleClose} className="absolute top-4 right-4">
          <CloseIcon />
        </IconButton>
        {loading ? (
          <p className="text-center">Chargement...</p>
        ) : callData ?  (
        
              <Box p={3}><h2 className="text-lg font-semibold mb-2">Informations sur l&apos;appel</h2><p className="text-sm text-gray-600">ID d&apos;appel: {callData.call_uuid}</p>
                  <Stack direction="row" spacing={1}>
              {callData.sentiment && <Chip
                  key={callData.sentiment}
                  label={callData.sentiment}
                  icon={<Heart size={16} color="white" />}
                  sx={{
                    ...chipStyle,
                    fontWeight: "bold",
                    borderRadius: "16px",
                  }}
                />}
                 {callData.recording_short_url && <Chip
                  key={callData.recording_short_url}
                  label="Écouter l'appel"
                  icon={<Headphones size={16} color="black" />}
                  sx={{
                    backgroundColor: "#E0E0E0",
                    color: "#000",
                    fontWeight: "bold",
                    borderRadius: "16px",
                  }}
                  onClick={() => window.open(callData.recording_short_url, "_blank")}
                />}
              </Stack>
              <div className="mt-4">
              {callData.user_name && (
                  <>
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <User size={16} /> Utilisateur
                    </h3>
                    <p className="text-gray-600 text-sm">{callData.user_name}</p>
                  </>
                )}
                    </div>
                    <div className="mt-4">
                        <h3 className="text-sm font-medium flex items-center gap-2">
                            <PhoneCall size={16} /> Appel {callData.direction === 'outbound' ? 'sortant' : 'entrant'}
                        </h3>
                        <p className="text-gray-600 text-sm">Client : {callData.phone_number}</p>
                        <p className="text-gray-600 text-sm">Commencé le : {format(new Date(callData.started_at), "dd/MM/yyyy 'à' HH:mm:ss")}</p>
                        <p className="text-gray-600 text-sm">Statut : {callData.answered_at ? "Répondu" : "Non répondu"}</p>
                    </div><div className="mt-4">
                        <h3 className="text-sm font-medium flex items-center gap-2">
                            <Clock size={16} /> Temps
                        </h3>
                        <p className="text-gray-600 text-sm">Durée de communication : {formatTime(callData.duration)}</p>
                    </div>
                    <div className="mt-4">
                        <h3 className="text-sm font-medium flex items-center gap-2">
                            <FileText size={16} /> Résumé
                        </h3>
                        <p className="text-gray-600 text-sm">{callData.summary || "Résumé non généré"}</p>
                    </div>
                    <div className="mt-4">
                        <h3 className="text-sm font-medium flex items-center gap-2">
                            <Megaphone size={16} /> Actions à entreprendre
                        </h3>
                        <Typography className="text-gray-600 text-sm" sx={{ whiteSpace: "pre-line", mb: 3 }}>{callData.topics || "Actions non générées"}</Typography>
                    </div>
                    <Accordion expanded={expandedTranscript} onChange={() => setExpandedTranscript(!expandedTranscript)}>
                    <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel4bh-content"
          id="panel4bh-header"
        >
             <h3 className="text-sm font-medium flex items-center gap-2">
                            <Scroll size={16} />   Transcription
                        </h3>
            </AccordionSummary>
            <AccordionDetails>
                    <div className="mt-4">
                     
                        <p className="text-gray-600 text-sm">{transcription.length > 0 ? <TranscriptionDisplay /> : <p>Transcription non générée</p>}</p>
                    </div>
                    </AccordionDetails>
                    </Accordion>
                    </Box>
) : (
          <p className="text-center text-red-500">Erreur lors du chargement des données.</p>
        )}
      </div>
    </Drawer>
  );
};



export default CustomerCallDetailsPanel;
